// The capability-split invariant (CDS doctrine) made legible for the Agents surface.
//
// No principal may hold BOTH context-read (INNER, context_bearing) AND egress (OUTER,
// egress_bearing) above threshold. That split is what keeps your context yours: an agent
// that can read your world can't also send out of it. `draft` is a third lane — the
// Familiar's authoring hand — never handed to any outside principal.
//
// Pure, dependency-free: the load-bearing rules live here so they can be unit-tested and
// reused by the mint form (which prevents a violating grant before it is ever made).

export type Lane = 'inner' | 'outer' | 'familiar'

export interface EffectClass {
  key: string
  /** Plain-words gloss: what this scope actually lets an agent DO. */
  what: string
  lane: Lane
  /** How it may be handed out: an Inner scope you grant, the gated Outer capability,
   *  or the Familiar's hand alone (never grantable). */
  grant: 'inner' | 'outer-gated' | 'familiar'
  context_bearing?: boolean
  egress_bearing?: boolean
}

// The five effect classes, ordered inner → familiar → outer so the reader crosses the
// membrane last. read is the context-bearing scope (INNER); egress is the new
// egress-bearing scope (OUTER), grantable only through the human-release gate.
export const EFFECT_CLASSES: EffectClass[] = [
  { key: 'read', lane: 'inner', grant: 'inner', context_bearing: true,
    what: 'See your world — the stars, signals, and domains you hold.' },
  { key: 'propose', lane: 'inner', grant: 'inner', context_bearing: true,
    what: 'Suggest at your gate. It can offer; only you approve.' },
  { key: 'attach', lane: 'inner', grant: 'inner', context_bearing: true,
    what: 'Add evidence to a mission step — citations, files, findings.' },
  { key: 'draft', lane: 'familiar', grant: 'familiar',
    what: "Author artifacts. This stays ✦ Tala's hand — never handed to an outside agent." },
  { key: 'egress', lane: 'outer', grant: 'outer-gated', egress_bearing: true,
    what: 'Send something out of your world — only for an Outer agent, and only when you release it at the gate.' },
]

const LANE_OF: Record<string, Lane> = Object.fromEntries(
  EFFECT_CLASSES.map((e) => [e.key, e.lane]),
)

/** The lane a single scope belongs to, or null for an unknown token. */
export function laneOf(scope: string): Lane | null {
  return LANE_OF[scope] ?? null
}

/** Does this set of scopes cross the membrane — holding both an INNER (context) scope
 *  and the OUTER (egress) scope at once? That is the forbidden combination. */
export function violatesSplit(scopes: string[]): boolean {
  let inner = false
  let outer = false
  for (const s of scopes) {
    const lane = laneOf(s)
    if (lane === 'inner') inner = true
    if (lane === 'outer') outer = true
  }
  return inner && outer
}

/** Which side of the membrane a principal sits on, from the scopes it holds:
 *  'inner' if it holds any context scope, 'outer' if it holds egress, null if neither.
 *  A set that mixes the two is invalid — callers should reject it via violatesSplit first. */
export function principalLane(scopes: string[]): Lane | null {
  const hasInner = scopes.some((s) => laneOf(s) === 'inner')
  const hasOuter = scopes.some((s) => laneOf(s) === 'outer')
  if (hasOuter && !hasInner) return 'outer'
  if (hasInner && !hasOuter) return 'inner'
  return null
}

/** May `scope` be added to a principal that already holds `current` without crossing the
 *  membrane? The mint form calls this to disable the checkbox that would violate the split. */
export function canAddScope(current: string[], scope: string): boolean {
  return !violatesSplit([...current, scope])
}
